// Implements: F-UIX-020
/**
 * The live preview of a profile (the mockup's "Vista previa en vivo"): a sample screen drawn with the
 * palette, the font stacks, the sizes, the radius and the spacing being edited. Pure presentation.
 *
 * The values reach the sample as custom properties set through `style.setProperty` (the CSP has no
 * `'unsafe-inline'` in `style-src`, so a `style=""` attribute is not an option). A value the preview
 * cannot draw (a half-typed colour) has its property removed and the sample falls back to a neutral,
 * so typing never paints something broken. The sample is a picture: assistive technology reads the
 * card's description instead of fake buttons and a fake form, and nothing in it takes the focus.
 */

import { useId, useLayoutEffect, useRef } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { ProfileVersionValues } from "../../../types/design";
import { previewVars } from "./previewTokens";
import styles from "./ProfilePreview.module.css";

export interface ProfilePreviewProps {
  readonly name: string;
  readonly values: ProfileVersionValues;
  /** The stored logo of the profile, when it has one. */
  readonly logoSrc?: string | undefined;
}

export function ProfilePreview({ name, values, logoSrc }: ProfilePreviewProps): ReactElement {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const sample = useRef<HTMLDivElement>(null);
  const applied = useRef<readonly string[]>([]);

  useLayoutEffect(() => {
    const element = sample.current;
    if (element === null) return;
    const vars = previewVars(values);
    for (const property of applied.current) {
      if (!(property in vars)) element.style.removeProperty(property);
    }
    for (const [property, value] of Object.entries(vars)) element.style.setProperty(property, value);
    applied.current = Object.keys(vars);
  }, [values]);

  return (
    <section className={styles.card} aria-labelledby={titleId} aria-describedby={descriptionId}>
      <div className={styles.head}>
        <h2 id={titleId} className={styles.title}>
          {t("design.preview.title")}
        </h2>
      </div>
      <p id={descriptionId} className={styles.description}>
        {t("design.preview.description")}
      </p>
      <div ref={sample} className={styles.sample} data-testid="design-preview-sample" aria-hidden="true">
        <div className={styles.bar}>
          {logoSrc === undefined ? <span className={styles.logo} /> : <img className={styles.logoImage} src={logoSrc} alt={""} />}
          <span>{name}</span>
        </div>
        <h3 className={styles.heading}>{t("design.preview.heading")}</h3>
        <p className={styles.body}>{t("design.preview.body")}</p>
        <div className={styles.buttons}>
          <span className={styles.primary}>{t("design.preview.primary")}</span>
          <span className={styles.secondary}>{t("design.preview.secondary")}</span>
        </div>
        <span className={styles.input}>{t("design.preview.input")}</span>
        <div className={styles.tile}>
          <span>{t("design.preview.tile")}</span>
          <span className={styles.tag}>{t("design.preview.tag")}</span>
        </div>
        <div className={styles.states}>
          <span className={styles.success}>{t("design.preview.success")}</span>
          <span className={styles.warning}>{t("design.preview.warning")}</span>
          <span className={styles.danger}>{t("design.preview.danger")}</span>
        </div>
      </div>
    </section>
  );
}
